/**
 * Apps2Go Hub – 15 lisämoduulin määrittelyt (1:1 ZIP-vastaavat).
 *
 * Jokainen `MODULE_DEFS` -kohta määrittelee:
 *  - label, icon, hue, color (sidebar)
 *  - api / collection (backend extra_modules)
 *  - fields (Add/Edit-lomake)
 *  - columns (taulukon kentät)
 *  - kpis (4 KPI-korttia ylhäällä)
 *  - tabs (välilehdet, filtteröinti)
 *
 * Kenttänimet vastaavat ZIPin Base44-entiteettejä.
 */
import {
  Briefcase, FolderOpen, Wrench, BarChart3, Video, Hammer, Sparkles,
  Users, Heart, FileText, ClipboardCheck, Boxes, CheckSquare,
  Calendar, Building, Wine, ShieldCheck, Brush, Lock, Clock, CheckCircle,
  TrendingUp, AlertTriangle, ChefHat, ClipboardList, AlertCircle,
  MapPin, Zap, Target, DollarSign, Calendar as CalendarIcon, Truck,
} from "lucide-react";

/** EUR formatter */
const eur = (v) =>
  new Intl.NumberFormat("fi-FI", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(v || 0));

const today = () => new Date().toISOString().split("T")[0];

const STATUS_COLORS = {
  // Generic stage colours
  uusi: "blue", new: "blue", suunnittelu: "blue", suunniteltu: "blue",
  kontaktoitu: "amber", in_progress: "amber", qualified: "amber",
  käynnissä: "amber", investigating: "amber", scheduled: "blue",
  open: "blue", contacted: "amber",
  proposal: "violet", neuvottelu: "violet", tarjous: "violet",
  kauppa: "emerald", won: "emerald", valmis: "emerald", completed: "emerald",
  active: "emerald", resolved: "emerald", signed: "emerald",
  menetetty: "red", lost: "red", cancelled: "red", critical: "red",
  pending: "amber", rented: "amber", overdue: "red",
  high: "violet", urgent: "red", low: "blue", medium: "amber",
  available: "emerald", maintenance: "amber", retired: "red",
};

export const MODULE_DEFS = {
  // ============================================================
  // 1) ADVANCED CRM – Lidit & myyntiputki
  // ============================================================
  advanced_crm: {
    key: "advanced_crm",
    label: "Edistynyt CRM",
    icon: Briefcase,
    hue: "from-fuchsia-400 to-purple-600",
    color: "text-fuchsia-300",
    path: "/advanced-crm",
    api: "/advanced-crm",
    collection: "advanced_crm_records",
    description: "Liiditunnistus, myyntiputki ja monikanavaiset asiakaspolut.",
    entityLabel: "Liidi",
    kpis: [
      { label: "Uudet lidit", icon: Zap, calc: (r) => r.filter(x => x.stage === "new").length },
      { label: "Pätevät lidit", icon: Target, accent: "emerald", calc: (r) => r.filter(x => x.stage === "qualified").length },
      { label: "Voitetut", icon: TrendingUp, accent: "violet", calc: (r) => r.filter(x => x.stage === "won").length },
      { label: "Putkiarvo", icon: DollarSign, accent: "amber", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.estimated_value) || 0), 0)) },
    ],
    fields: [
      { key: "company_name", label: "Yritys", required: true, col: 2 },
      { key: "contact_name", label: "Yhteyshenkilö" },
      { key: "contact_email", label: "Sähköposti", type: "email" },
      { key: "contact_phone", label: "Puhelin" },
      { key: "stage", label: "Vaihe", type: "select", options: [
        { value: "new", label: "Uusi" },
        { value: "contacted", label: "Kontaktoitu" },
        { value: "qualified", label: "Pätevä" },
        { value: "proposal", label: "Tarjous" },
        { value: "won", label: "Voitettu" },
        { value: "lost", label: "Menetetty" },
      ], default: "new" },
      { key: "estimated_value", label: "Arvioitu arvo €", type: "currency" },
      { key: "source", label: "Lähde", placeholder: "verkkosivut, suosittelu, mainos..." },
      { key: "next_action_date", label: "Seuraava toimenpide", type: "date" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "company_name", label: "Yritys" },
      { key: "contact_name", label: "Yhteyshenkilö" },
      { key: "stage", label: "Vaihe", type: "badge", color: STATUS_COLORS },
      { key: "estimated_value", label: "Arvo", type: "currency" },
    ],
    tabs: [
      { value: "pipeline", label: "Myyntiputki" },
      { value: "all", label: "Kaikki lidit" },
      { value: "won", label: "Voitetut", filter: (r) => r.stage === "won" },
    ],
  },

  // ============================================================
  // 2) CLEANING – Siivouspalvelut
  // ============================================================
  cleaning: {
    key: "cleaning",
    label: "Siivouspalvelut",
    icon: Brush,
    hue: "from-blue-400 to-cyan-600",
    color: "text-blue-300",
    path: "/cleaning",
    api: "/cleaning",
    collection: "cleaning_jobs",
    description: "Siivoussopimusten, aikataulun ja tiimien hallinta.",
    entityLabel: "Siivoustyö",
    kpis: [
      { label: "Tänään", icon: Clock, calc: (r) => r.filter(x => x.scheduled_date === today() && x.status !== "cancelled").length },
      { label: "Valmistuneet", icon: Sparkles, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Asiakkaita", icon: Users, accent: "violet", calc: (r) => new Set(r.map(x => x.client_name)).size },
      { label: "Tulot", icon: DollarSign, accent: "amber", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.billing_amount) || 0), 0)) },
    ],
    fields: [
      { key: "client_name", label: "Asiakas", required: true, col: 2 },
      { key: "location", label: "Osoite", col: 2 },
      { key: "scheduled_date", label: "Päivä", type: "date", required: true },
      { key: "scheduled_time", label: "Aika", placeholder: "08:00" },
      { key: "cleaning_type", label: "Siivouksen tyyppi", type: "select", options: [
        "regular", "deep_clean", "window", "carpet", "post_construction", "office",
      ], default: "regular" },
      { key: "square_meters", label: "Pinta-ala m²", type: "number" },
      { key: "team_size", label: "Tiimikoko", type: "number", default: 1 },
      { key: "duration_hours", label: "Kesto (h)", type: "number" },
      { key: "billing_amount", label: "Hinta €", type: "currency" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "scheduled", label: "Aikataulutettu" },
        { value: "in_progress", label: "Käynnissä" },
        { value: "completed", label: "Valmis" },
        { value: "cancelled", label: "Peruutettu" },
      ], default: "scheduled" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "client_name", label: "Asiakas" },
      { key: "scheduled_date", label: "Päivä", type: "date" },
      { key: "cleaning_type", label: "Tyyppi" },
      { key: "square_meters", label: "m²" },
      { key: "billing_amount", label: "Hinta", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "schedule", label: "Aikataulu", filter: (r) => r.status !== "cancelled" },
      { value: "all", label: "Kaikki työt" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 3) CONSTRUCTION – Rakennusprojektit
  // ============================================================
  construction: {
    key: "construction",
    label: "Rakennusprojektit",
    icon: Hammer,
    hue: "from-orange-400 to-red-600",
    color: "text-orange-300",
    path: "/construction",
    api: "/construction",
    collection: "construction_projects",
    description: "Rakennusprojektien, budjetin ja työmaiden hallinta.",
    entityLabel: "Projekti",
    kpis: [
      { label: "Aktiiviset", icon: Hammer, calc: (r) => r.filter(x => x.status === "in_progress").length },
      { label: "Valmistuneet", icon: TrendingUp, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Budjetit", icon: DollarSign, accent: "amber", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.budget) || 0), 0)) },
      { label: "Turvallisuuspoik.", icon: AlertTriangle, accent: "red", calc: (r) => r.reduce((s, x) => s + (parseInt(x.safety_incidents) || 0), 0) },
    ],
    fields: [
      { key: "project_name", label: "Projektin nimi", required: true, col: 2 },
      { key: "location", label: "Sijainti", col: 2 },
      { key: "project_type", label: "Tyyppi", type: "select", options: [
        "new_build", "renovation", "infrastructure", "industrial", "residential",
      ] },
      { key: "client", label: "Asiakas" },
      { key: "project_manager", label: "Projektipäällikkö" },
      { key: "budget", label: "Budjetti €", type: "currency" },
      { key: "spent", label: "Käytetty €", type: "currency" },
      { key: "start_date", label: "Aloitus", type: "date" },
      { key: "end_date", label: "Tavoite", type: "date" },
      { key: "progress_percent", label: "Valmis %", type: "number" },
      { key: "safety_incidents", label: "Turvallisuuspoikkeamia", type: "number", default: 0 },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "planning", label: "Suunnittelu" },
        { value: "in_progress", label: "Käynnissä" },
        { value: "completed", label: "Valmis" },
        { value: "on_hold", label: "Tauolla" },
      ], default: "planning" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "project_name", label: "Projekti" },
      { key: "client", label: "Asiakas" },
      { key: "location", label: "Sijainti" },
      { key: "progress_percent", label: "Edistyminen", type: "progress" },
      { key: "budget", label: "Budjetti", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "projects", label: "Projektit" },
      { value: "progress", label: "Edistyminen", filter: (r) => r.status !== "planning" },
    ],
  },

  // ============================================================
  // 4) DMS – Dokumenttihallinta
  // ============================================================
  dms: {
    key: "dms",
    label: "Dokumenttihallinta",
    icon: FileText,
    hue: "from-slate-400 to-gray-600",
    color: "text-slate-300",
    path: "/dms",
    api: "/dms",
    collection: "documents",
    description: "Versionhallittu dokumenttiarkisto ja allekirjoitukset.",
    entityLabel: "Dokumentti",
    kpis: [
      { label: "Dokumentteja", icon: FileText, calc: (r) => r.length },
      { label: "Odottaa allekirj.", icon: Clock, accent: "amber", calc: (r) => r.filter(x => x.signature_status === "pending").length },
      { label: "Allekirjoitetut", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.signature_status === "signed").length },
      { label: "Vanhenemassa", icon: Lock, accent: "red", calc: (r) => r.filter(x => {
        if (!x.expiry_date) return false;
        const d = Math.floor((new Date(x.expiry_date) - new Date()) / (1000 * 60 * 60 * 24));
        return d > 0 && d <= 30;
      }).length },
    ],
    fields: [
      { key: "title", label: "Otsikko", required: true, col: 2 },
      { key: "document_type", label: "Tyyppi", type: "select", options: [
        "contract", "invoice", "report", "policy", "manual", "other",
      ], default: "other" },
      { key: "category", label: "Kategoria", placeholder: "sopimus, lasku, raportti..." },
      { key: "url", label: "Linkki / tiedosto", col: 2 },
      { key: "version", label: "Versio", default: "1.0" },
      { key: "status", label: "Tila", type: "select", options: [
        "draft", "active", "archived",
      ], default: "draft" },
      { key: "signature_status", label: "Allekirj. tila", type: "select", options: [
        { value: "not_required", label: "Ei vaadittu" },
        { value: "pending", label: "Odottaa" },
        { value: "signed", label: "Allekirjoitettu" },
      ], default: "not_required" },
      { key: "shared_with", label: "Jaettu (sähköposteja)" },
      { key: "expiry_date", label: "Vanhenee", type: "date" },
      { key: "tags", label: "Tagit (pilkulla)" },
      { key: "description", label: "Kuvaus", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "title", label: "Otsikko" },
      { key: "document_type", label: "Tyyppi" },
      { key: "version", label: "Versio" },
      { key: "signature_status", label: "Allekirj.", type: "badge", color: STATUS_COLORS },
      { key: "expiry_date", label: "Vanhenee", type: "date" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "all", label: "Kaikki" },
      { value: "pending", label: "Odottaa allekirj.", filter: (r) => r.signature_status === "pending" },
      { value: "signed", label: "Allekirjoitetut", filter: (r) => r.signature_status === "signed" },
    ],
  },

  // ============================================================
  // 5) EVENT MANAGEMENT – Tapahtumat
  // ============================================================
  event_management: {
    key: "event_management",
    label: "Tapahtumat",
    icon: CalendarIcon,
    hue: "from-pink-400 to-rose-600",
    color: "text-pink-300",
    path: "/events",
    api: "/events",
    collection: "events",
    description: "Tapahtumien suunnittelu, osallistujat ja tilavaraukset.",
    entityLabel: "Tapahtuma",
    kpis: [
      { label: "Tulevat", icon: Calendar, calc: (r) => r.filter(x => x.event_date && new Date(x.event_date) >= new Date() && x.status !== "cancelled").length },
      { label: "Valmistuneet", icon: Sparkles, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Tulo", icon: DollarSign, accent: "amber", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.total_amount) || 0), 0)) },
      { label: "Keski-osallistujat", icon: Users, accent: "violet", calc: (r) => r.length ? Math.round(r.reduce((s, x) => s + (parseInt(x.expected_guests) || 0), 0) / r.length) : 0 },
    ],
    fields: [
      { key: "event_name", label: "Tapahtuma", required: true, col: 2 },
      { key: "client_name", label: "Asiakas" },
      { key: "event_type", label: "Tyyppi", type: "select", options: [
        "wedding", "corporate", "conference", "concert", "festival", "private", "other",
      ] },
      { key: "venue", label: "Paikka" },
      { key: "event_date", label: "Päivä", type: "date", required: true },
      { key: "start_time", label: "Alkaa", placeholder: "18:00" },
      { key: "end_time", label: "Päättyy", placeholder: "23:00" },
      { key: "expected_guests", label: "Vieraita", type: "number" },
      { key: "registered_guests", label: "Ilmoittautuneita", type: "number" },
      { key: "total_amount", label: "Summa €", type: "currency" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "draft", label: "Luonnos" },
        { value: "open", label: "Avoinna" },
        { value: "sold_out", label: "Loppuunmyyty" },
        { value: "completed", label: "Valmis" },
        { value: "cancelled", label: "Peruutettu" },
      ], default: "draft" },
      { key: "description", label: "Kuvaus", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "event_name", label: "Tapahtuma" },
      { key: "event_date", label: "Päivä", type: "date" },
      { key: "expected_guests", label: "Vieraita" },
      { key: "total_amount", label: "Summa", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "upcoming", label: "Tulevat", filter: (r) => r.event_date && new Date(r.event_date) >= new Date() && r.status !== "cancelled" },
      { value: "all", label: "Kaikki" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 6) FIELD SERVICE – Kenttätyöt
  // ============================================================
  field_service: {
    key: "field_service",
    label: "Kenttätyöt",
    icon: Wrench,
    hue: "from-red-400 to-pink-600",
    color: "text-red-300",
    path: "/field-service",
    api: "/field-service",
    collection: "field_service_jobs",
    description: "Kenttäteknikoiden työmääräykset ja reititys.",
    entityLabel: "Työtilaus",
    kpis: [
      { label: "Avoimet", icon: AlertCircle, calc: (r) => r.filter(x => x.status === "open").length },
      { label: "Käynnissä", icon: Clock, accent: "amber", calc: (r) => r.filter(x => x.status === "in_progress").length },
      { label: "Valmis", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Tulo €", icon: DollarSign, accent: "violet", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.total_amount) || 0), 0)) },
    ],
    fields: [
      { key: "work_order_number", label: "Työtilausnro", placeholder: "Auto-generoidaan", col: 2 },
      { key: "customer_name", label: "Asiakas", required: true },
      { key: "service_type", label: "Palvelu", type: "select", options: [
        "installation", "repair", "maintenance", "inspection", "emergency",
      ] },
      { key: "address", label: "Osoite", col: 2 },
      { key: "assigned_technician", label: "Teknikko" },
      { key: "scheduled_date", label: "Päivä", type: "date" },
      { key: "estimated_hours", label: "Arvio (h)", type: "number" },
      { key: "priority", label: "Prioriteetti", type: "select", options: [
        { value: "low", label: "Matala" },
        { value: "normal", label: "Normaali" },
        { value: "high", label: "Korkea" },
        { value: "urgent", label: "Kiireellinen" },
      ], default: "normal" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "open", label: "Avoin" },
        { value: "assigned", label: "Määrätty" },
        { value: "in_progress", label: "Käynnissä" },
        { value: "completed", label: "Valmis" },
        { value: "cancelled", label: "Peruutettu" },
      ], default: "open" },
      { key: "total_amount", label: "Summa €", type: "currency" },
      { key: "description", label: "Kuvaus", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "work_order_number", label: "Nro" },
      { key: "customer_name", label: "Asiakas" },
      { key: "assigned_technician", label: "Teknikko" },
      { key: "scheduled_date", label: "Päivä", type: "date" },
      { key: "priority", label: "Prio", type: "badge", color: STATUS_COLORS },
      { key: "total_amount", label: "Summa", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "active", label: "Aktiiviset", filter: (r) => r.status === "in_progress" },
      { value: "all", label: "Kaikki työt" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 7) FOOD & BEVERAGE – Reseptit
  // ============================================================
  food_beverage: {
    key: "food_beverage",
    label: "Catering / F&B",
    icon: Wine,
    hue: "from-rose-400 to-orange-600",
    color: "text-rose-300",
    path: "/food-beverage",
    api: "/food-beverage",
    collection: "food_beverage_orders",
    description: "Reseptit, annoshinnat ja katelaskenta.",
    entityLabel: "Resepti",
    kpis: [
      { label: "Reseptejä", icon: ChefHat, calc: (r) => r.filter(x => x.is_active !== false).length },
      { label: "Kate-%", icon: TrendingUp, accent: "emerald", calc: (r) => r.length ? Math.round(r.reduce((s, x) => s + (parseFloat(x.margin_percent) || 0), 0) / r.length) + "%" : "0%" },
      { label: "Kustannus ka.", icon: DollarSign, accent: "amber", calc: (r) => r.length ? eur(r.reduce((s, x) => s + (parseFloat(x.cost_per_portion) || 0), 0) / r.length) : eur(0) },
      { label: "Myynti ka.", icon: DollarSign, accent: "violet", calc: (r) => r.length ? eur(r.reduce((s, x) => s + (parseFloat(x.selling_price) || 0), 0) / r.length) : eur(0) },
    ],
    fields: [
      { key: "recipe_name", label: "Reseptin nimi", required: true, col: 2 },
      { key: "category", label: "Kategoria", type: "select", options: [
        { value: "appetizer", label: "Alkuruoka" },
        { value: "main_course", label: "Pääruoka" },
        { value: "dessert", label: "Jälkiruoka" },
        { value: "beverage", label: "Juoma" },
        { value: "side_dish", label: "Lisäke" },
      ] },
      { key: "portion_size", label: "Annoskoko" },
      { key: "cost_per_portion", label: "Kustannus / annos €", type: "currency" },
      { key: "selling_price", label: "Myyntihinta €", type: "currency" },
      { key: "margin_percent", label: "Kate %", type: "number" },
      { key: "ingredients", label: "Raaka-aineet", col: 2, type: "textarea" },
      { key: "preparation", label: "Valmistus", col: 2, type: "textarea" },
      { key: "allergens", label: "Allergeenit", col: 2 },
    ],
    columns: [
      { key: "recipe_name", label: "Resepti" },
      { key: "category", label: "Kategoria" },
      { key: "cost_per_portion", label: "Kustannus", type: "currency" },
      { key: "selling_price", label: "Myynti", type: "currency" },
      { key: "margin_percent", label: "Kate", suffix: "%" },
    ],
    tabs: [
      { value: "recipes", label: "Reseptit" },
      { value: "main", label: "Pääruoat", filter: (r) => r.category === "main_course" },
      { value: "drinks", label: "Juomat", filter: (r) => r.category === "beverage" },
    ],
  },

  // ============================================================
  // 8) HOME CARE – Kotipalvelu
  // ============================================================
  home_care: {
    key: "home_care",
    label: "Kotipalvelu",
    icon: Heart,
    hue: "from-rose-400 to-pink-600",
    color: "text-rose-300",
    path: "/home-care",
    api: "/home-care",
    collection: "home_care_visits",
    description: "Kotihoidon käynnit, hoitajat ja hoitosuunnitelmat.",
    entityLabel: "Käynti",
    kpis: [
      { label: "Tänään", icon: Clock, calc: (r) => r.filter(x => x.visit_date === today()).length },
      { label: "Potilaita", icon: Heart, accent: "red", calc: (r) => new Set(r.map(x => x.patient_id || x.patient_name)).size },
      { label: "Valmiit", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Tunnit yht.", icon: Clock, accent: "amber", calc: (r) => Math.round(r.reduce((s, x) => s + (parseFloat(x.duration_minutes) || 0), 0) / 60) },
    ],
    fields: [
      { key: "patient_name", label: "Potilas", required: true, col: 2 },
      { key: "patient_id", label: "Potilas-ID" },
      { key: "caregiver_name", label: "Hoitaja" },
      { key: "address", label: "Osoite", col: 2 },
      { key: "visit_date", label: "Käyntipäivä", type: "date", required: true },
      { key: "start_time", label: "Alkaa", placeholder: "08:00" },
      { key: "end_time", label: "Päättyy", placeholder: "09:30" },
      { key: "duration_minutes", label: "Kesto (min)", type: "number" },
      { key: "service_type", label: "Palvelu", type: "select", options: [
        { value: "personal_care", label: "Henkilökohtainen hoito" },
        { value: "medical", label: "Sairaanhoito" },
        { value: "meal", label: "Ateriapalvelu" },
        { value: "cleaning", label: "Siivous" },
        { value: "company", label: "Seuranpalvelu" },
        { value: "other", label: "Muu" },
      ], default: "personal_care" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "scheduled", label: "Aikataulutettu" },
        { value: "completed", label: "Valmis" },
        { value: "cancelled", label: "Peruutettu" },
      ], default: "scheduled" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "patient_name", label: "Potilas" },
      { key: "caregiver_name", label: "Hoitaja" },
      { key: "visit_date", label: "Päivä", type: "date" },
      { key: "service_type", label: "Palvelu" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "schedule", label: "Aikataulu", filter: (r) => r.status !== "cancelled" },
      { value: "patients", label: "Potilaat" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 9) MEETING – Kokoushuone
  // ============================================================
  meeting: {
    key: "meeting",
    label: "Kokoushuone",
    icon: Video,
    hue: "from-cyan-400 to-sky-600",
    color: "text-cyan-300",
    path: "/meeting",
    api: "/meeting",
    collection: "meeting_rooms",
    description: "Kokoushuonevaraukset, osallistujat ja videoneuvottelut.",
    entityLabel: "Varaus",
    kpis: [
      { label: "Varauksia", icon: Video, calc: (r) => r.length },
      { label: "Tänään", icon: Clock, accent: "amber", calc: (r) => r.filter(x => (x.starts_at || "").slice(0, 10) === today()).length },
      { label: "Valmiit", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
      { label: "Aktiiviset", icon: Users, accent: "violet", calc: (r) => r.filter(x => x.status === "in_progress").length },
    ],
    fields: [
      { key: "title", label: "Aihe", required: true, col: 2 },
      { key: "room_name", label: "Huone" },
      { key: "organizer", label: "Järjestäjä" },
      { key: "starts_at", label: "Alkaa", type: "datetime-local", required: true },
      { key: "ends_at", label: "Päättyy", type: "datetime-local" },
      { key: "participants", label: "Osallistujat (pilkulla)", col: 2 },
      { key: "meeting_link", label: "Video-linkki", col: 2 },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "scheduled", label: "Aikataulutettu" },
        { value: "in_progress", label: "Käynnissä" },
        { value: "completed", label: "Valmis" },
        { value: "cancelled", label: "Peruutettu" },
      ], default: "scheduled" },
      { key: "agenda", label: "Agenda", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "title", label: "Aihe" },
      { key: "room_name", label: "Huone" },
      { key: "starts_at", label: "Alkaa", type: "datetime" },
      { key: "organizer", label: "Järjestäjä" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "upcoming", label: "Tulevat", filter: (r) => r.status === "scheduled" },
      { value: "all", label: "Kaikki" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 10) PROPERTY MANAGEMENT – Kiinteistöhallinta
  // ============================================================
  property_management: {
    key: "property_management",
    label: "Kiinteistöhallinta",
    icon: Building,
    hue: "from-stone-400 to-amber-600",
    color: "text-amber-300",
    path: "/property",
    api: "/property",
    collection: "properties",
    description: "Kiinteistöt, vuokralaiset ja huoltoaikataulut.",
    entityLabel: "Kiinteistö",
    kpis: [
      { label: "Kiinteistöjä", icon: Building, calc: (r) => r.filter(x => x.status === "active").length },
      { label: "Huoneistoja", icon: TrendingUp, accent: "emerald", calc: (r) => r.reduce((s, x) => s + (parseInt(x.units) || 0), 0) },
      { label: "Huolto odottaa", icon: Wrench, accent: "amber", calc: (r) => r.filter(x => x.maintenance_due).length },
      { label: "Budjetti", icon: DollarSign, accent: "violet", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.annual_budget) || 0), 0)) },
    ],
    fields: [
      { key: "property_name", label: "Kiinteistö", required: true, col: 2 },
      { key: "address", label: "Osoite", col: 2 },
      { key: "property_type", label: "Tyyppi", type: "select", options: [
        { value: "apartment", label: "Asunto" },
        { value: "commercial", label: "Liiketila" },
        { value: "office", label: "Toimisto" },
        { value: "warehouse", label: "Varasto" },
        { value: "industrial", label: "Teollisuus" },
      ] },
      { key: "square_meters", label: "Pinta-ala (m²)", type: "number" },
      { key: "units", label: "Huoneistoja", type: "number" },
      { key: "annual_budget", label: "Vuosibudjetti €", type: "currency" },
      { key: "rent_amount", label: "Vuokra €/kk", type: "currency" },
      { key: "tenant_name", label: "Vuokralainen" },
      { key: "tenant_email", label: "Vuokralaisen sähköposti" },
      { key: "lease_start", label: "Vuokra alkaa", type: "date" },
      { key: "lease_end", label: "Vuokra päättyy", type: "date" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "active", label: "Aktiivinen" },
        { value: "vacant", label: "Vapaa" },
        { value: "renovation", label: "Remontissa" },
      ], default: "active" },
    ],
    columns: [
      { key: "property_name", label: "Kiinteistö" },
      { key: "tenant_name", label: "Vuokralainen" },
      { key: "rent_amount", label: "Vuokra", type: "currency" },
      { key: "lease_end", label: "Päättyy", type: "date" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "properties", label: "Kiinteistöt" },
      { value: "maintenance", label: "Huolto", filter: (r) => r.maintenance_due },
    ],
  },

  // ============================================================
  // 11) PSA – Asiantuntijapalvelut (sopimukset)
  // ============================================================
  psa: {
    key: "psa",
    label: "Asiantuntijapalvelut",
    icon: Briefcase,
    hue: "from-violet-400 to-fuchsia-600",
    color: "text-violet-300",
    path: "/psa",
    api: "/psa",
    collection: "psa_projects",
    description: "Konsultti- ja palvelusopimusten elinkaaren hallinta.",
    entityLabel: "Sopimus",
    kpis: [
      { label: "Aktiiviset", icon: Briefcase, calc: (r) => r.filter(x => x.status === "active").length },
      { label: "Kate yht.", icon: TrendingUp, accent: "emerald", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.margin_amount) || 0), 0)) },
      { label: "Budjetissa", icon: Target, accent: "violet", calc: (r) => r.filter(x => (x.actual_cost || 0) <= (x.budget || x.contract_value || 0)).length },
      { label: "Ylittyneet", icon: AlertCircle, accent: "red", calc: (r) => r.filter(x => (x.actual_cost || 0) > (x.budget || x.contract_value || 0)).length },
    ],
    fields: [
      { key: "contract_number", label: "Sopimusnro", placeholder: "Auto-generoidaan" },
      { key: "project_name", label: "Projekti", required: true },
      { key: "customer_name", label: "Asiakas" },
      { key: "project_manager", label: "Vastuuhenkilö" },
      { key: "contract_type", label: "Tyyppi", type: "select", options: [
        { value: "time_material", label: "Time & Material" },
        { value: "fixed_price", label: "Kiinteähinta" },
        { value: "retainer", label: "Retainer" },
        { value: "milestone", label: "Vaiheittainen" },
      ] },
      { key: "contract_value", label: "Sopimusarvo €", type: "currency" },
      { key: "budget", label: "Budjetti €", type: "currency" },
      { key: "actual_cost", label: "Toteutunut €", type: "currency" },
      { key: "margin_amount", label: "Kate €", type: "currency" },
      { key: "margin_percent", label: "Kate %", type: "number" },
      { key: "estimated_hours", label: "Arvio (h)", type: "number" },
      { key: "actual_hours", label: "Toteutuneet (h)", type: "number" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "draft", label: "Luonnos" },
        { value: "active", label: "Aktiivinen" },
        { value: "on_hold", label: "Tauolla" },
        { value: "completed", label: "Valmis" },
      ], default: "draft" },
    ],
    columns: [
      { key: "contract_number", label: "Nro" },
      { key: "customer_name", label: "Asiakas" },
      { key: "project_name", label: "Projekti" },
      { key: "contract_value", label: "Arvo", type: "currency" },
      { key: "margin_amount", label: "Kate", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "active", label: "Aktiiviset", filter: (r) => r.status === "active" },
      { value: "all", label: "Kaikki" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },

  // ============================================================
  // 12) QMS – Laadunhallinta
  // ============================================================
  qms: {
    key: "qms",
    label: "Laadunhallinta",
    icon: ClipboardCheck,
    hue: "from-emerald-400 to-teal-600",
    color: "text-emerald-300",
    path: "/qms",
    api: "/qms",
    collection: "qms_records",
    description: "Poikkeamat, auditoinnit ja jatkuva parantaminen.",
    entityLabel: "Poikkeama",
    kpis: [
      { label: "Avoimet", icon: AlertTriangle, accent: "amber", calc: (r) => r.filter(x => ["open", "investigating"].includes(x.status)).length },
      { label: "Kriittiset", icon: AlertCircle, accent: "red", calc: (r) => r.filter(x => x.severity === "critical").length },
      { label: "Ratkaistut", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => ["resolved", "closed"].includes(x.status)).length },
      { label: "Auditoinnit", icon: ClipboardList, accent: "violet", calc: (r) => r.filter(x => x.record_type === "audit").length },
    ],
    fields: [
      { key: "incident_number", label: "Numero", placeholder: "Auto-generoidaan" },
      { key: "record_type", label: "Tyyppi", type: "select", options: [
        { value: "incident", label: "Poikkeama" },
        { value: "audit", label: "Auditointi" },
        { value: "inspection", label: "Tarkastus" },
        { value: "improvement", label: "Parantaminen" },
      ], default: "incident" },
      { key: "title", label: "Otsikko", required: true, col: 2 },
      { key: "module", label: "Moduuli", placeholder: "Tuotanto / Lähetys..." },
      { key: "severity", label: "Vakavuus", type: "select", options: [
        { value: "low", label: "Matala" },
        { value: "medium", label: "Keskinkertainen" },
        { value: "high", label: "Korkea" },
        { value: "critical", label: "Kriittinen" },
      ], default: "medium" },
      { key: "reported_by", label: "Ilmoittaja" },
      { key: "responsible", label: "Vastuuhenkilö" },
      { key: "due_date", label: "Eräpäivä", type: "date" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "open", label: "Avoin" },
        { value: "investigating", label: "Selvityksessä" },
        { value: "in_progress", label: "Käsittelyssä" },
        { value: "resolved", label: "Ratkaistu" },
        { value: "closed", label: "Suljettu" },
      ], default: "open" },
      { key: "description", label: "Kuvaus", col: 2, type: "textarea" },
      { key: "corrective_action", label: "Korjaava toimenpide", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "title", label: "Otsikko" },
      { key: "record_type", label: "Tyyppi" },
      { key: "module", label: "Moduuli" },
      { key: "severity", label: "Vakavuus", type: "badge", color: STATUS_COLORS },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "incidents", label: "Poikkeamat", filter: (r) => r.record_type !== "audit" },
      { value: "audits", label: "Auditoinnit", filter: (r) => r.record_type === "audit" },
      { value: "critical", label: "Kriittiset", filter: (r) => r.severity === "critical" },
    ],
  },

  // ============================================================
  // 13) RENTAL – Vuokraus (kalusto)
  // ============================================================
  rental: {
    key: "rental",
    label: "Vuokraus",
    icon: Boxes,
    hue: "from-indigo-400 to-blue-600",
    color: "text-indigo-300",
    path: "/rental",
    api: "/rental",
    collection: "rental_items",
    description: "Vuokrakaluston hallinta, varaukset ja saatavuus.",
    entityLabel: "Vuokrakohde",
    kpis: [
      { label: "Aktiiviset vuokrat", icon: Truck, calc: (r) => r.filter(x => x.status === "rented").length },
      { label: "Saatavilla", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.status === "available").length },
      { label: "Huollossa", icon: Wrench, accent: "amber", calc: (r) => r.filter(x => x.status === "maintenance").length },
      { label: "Vuokratulo", icon: DollarSign, accent: "violet", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.total_revenue) || 0), 0)) },
    ],
    fields: [
      { key: "equipment_name", label: "Kalusto", required: true, col: 2 },
      { key: "equipment_code", label: "Tunnus / sarjanumero" },
      { key: "category", label: "Kategoria" },
      { key: "daily_rental_rate", label: "Päivähinta €", type: "currency" },
      { key: "weekly_rental_rate", label: "Viikkohinta €", type: "currency" },
      { key: "current_value", label: "Arvo €", type: "currency" },
      { key: "current_renter", label: "Nykyinen vuokralainen" },
      { key: "rented_until", label: "Vapautuu", type: "date" },
      { key: "total_revenue", label: "Kokonaistulo €", type: "currency" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "available", label: "Saatavilla" },
        { value: "rented", label: "Vuokrattu" },
        { value: "maintenance", label: "Huollossa" },
        { value: "retired", label: "Poistettu" },
      ], default: "available" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "equipment_name", label: "Kalusto" },
      { key: "equipment_code", label: "Koodi" },
      { key: "current_renter", label: "Vuokralainen" },
      { key: "daily_rental_rate", label: "€/pv", type: "currency" },
      { key: "rented_until", label: "Vapautuu", type: "date" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "bookings", label: "Aktiiviset", filter: (r) => r.status === "rented" },
      { value: "equipment", label: "Kalusto" },
      { value: "available", label: "Saatavilla", filter: (r) => r.status === "available" },
    ],
  },

  // ============================================================
  // 14) REPORTING – Kuljetusraportointi
  // ============================================================
  reporting: {
    key: "reporting",
    label: "Kuljetusraportointi",
    icon: BarChart3,
    hue: "from-violet-400 to-purple-600",
    color: "text-violet-300",
    path: "/reporting",
    api: "/reporting",
    collection: "driver_reports",
    description: "Kuljettajien työpäivät, kilometrit ja polttoaineet.",
    entityLabel: "Päiväraportti",
    kpis: [
      { label: "Raportteja", icon: BarChart3, calc: (r) => r.length },
      { label: "Kuljettajia", icon: Users, accent: "violet", calc: (r) => new Set(r.map(x => x.driver_name)).size },
      { label: "Km yht.", icon: MapPin, accent: "emerald", calc: (r) => r.reduce((s, x) => s + (parseFloat(x.total_km) || 0), 0).toFixed(0) },
      { label: "Polttoaine €", icon: DollarSign, accent: "amber", calc: (r) => eur(r.reduce((s, x) => s + (parseFloat(x.fuel_cost) || 0), 0)) },
    ],
    fields: [
      { key: "driver_name", label: "Kuljettaja", required: true },
      { key: "vehicle", label: "Ajoneuvo" },
      { key: "report_date", label: "Päivä", type: "date", required: true },
      { key: "start_odometer", label: "Aloitus km", type: "number" },
      { key: "end_odometer", label: "Lopetus km", type: "number" },
      { key: "total_km", label: "Yhteensä km", type: "number" },
      { key: "fuel_liters", label: "Polttoaine (L)", type: "number" },
      { key: "fuel_cost", label: "Polttoaine €", type: "currency" },
      { key: "deliveries_count", label: "Toimituksia", type: "number" },
      { key: "earnings", label: "Tulo €", type: "currency" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "pending", label: "Odottaa" },
        { value: "approved", label: "Hyväksytty" },
        { value: "discrepancy", label: "Eroavaisuus" },
      ], default: "pending" },
      { key: "notes", label: "Muistiinpanot", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "report_date", label: "Päivä", type: "date" },
      { key: "driver_name", label: "Kuljettaja" },
      { key: "vehicle", label: "Ajoneuvo" },
      { key: "total_km", label: "km" },
      { key: "fuel_cost", label: "Polttoaine", type: "currency" },
      { key: "earnings", label: "Tulot", type: "currency" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "all", label: "Kaikki" },
      { value: "pending", label: "Odottaa", filter: (r) => r.status === "pending" },
      { value: "approved", label: "Hyväksytyt", filter: (r) => r.status === "approved" },
    ],
  },

  // ============================================================
  // 15) TASKS – Tehtävät
  // ============================================================
  tasks: {
    key: "tasks",
    label: "Tehtävät",
    icon: CheckSquare,
    hue: "from-amber-300 to-yellow-500",
    color: "text-amber-300",
    path: "/tasks",
    api: "/tasks",
    collection: "tasks",
    description: "Yritystason tehtävälistat ja delegointi.",
    entityLabel: "Tehtävä",
    kpis: [
      { label: "Kaikki", icon: ClipboardList, calc: (r) => r.length },
      { label: "Avoimet", icon: AlertCircle, accent: "amber", calc: (r) => r.filter(x => x.status === "open").length },
      { label: "Käynnissä", icon: Clock, accent: "violet", calc: (r) => r.filter(x => x.status === "in_progress").length },
      { label: "Valmiit", icon: CheckCircle, accent: "emerald", calc: (r) => r.filter(x => x.status === "completed").length },
    ],
    fields: [
      { key: "title", label: "Otsikko", required: true, col: 2 },
      { key: "assigned_to_name", label: "Vastuuhenkilö" },
      { key: "due_date", label: "Eräpäivä", type: "date" },
      { key: "priority", label: "Prioriteetti", type: "select", options: [
        { value: "low", label: "Matala" },
        { value: "medium", label: "Keskiverto" },
        { value: "high", label: "Korkea" },
        { value: "urgent", label: "Kiireellinen" },
      ], default: "medium" },
      { key: "status", label: "Tila", type: "select", options: [
        { value: "open", label: "Avoin" },
        { value: "in_progress", label: "Käynnissä" },
        { value: "completed", label: "Valmis" },
        { value: "blocked", label: "Estynyt" },
      ], default: "open" },
      { key: "progress_percent", label: "Edistyminen %", type: "number" },
      { key: "description", label: "Kuvaus", col: 2, type: "textarea" },
    ],
    columns: [
      { key: "title", label: "Tehtävä" },
      { key: "assigned_to_name", label: "Vastuussa" },
      { key: "due_date", label: "Eräpäivä", type: "date" },
      { key: "priority", label: "Prio", type: "badge", color: STATUS_COLORS },
      { key: "progress_percent", label: "Edistyminen", type: "progress" },
      { key: "status", label: "Tila", type: "badge", color: STATUS_COLORS },
    ],
    tabs: [
      { value: "all", label: "Kaikki" },
      { value: "open", label: "Avoimet", filter: (r) => r.status === "open" },
      { value: "in_progress", label: "Käynnissä", filter: (r) => r.status === "in_progress" },
      { value: "completed", label: "Valmiit", filter: (r) => r.status === "completed" },
    ],
  },
};

export const ALL_MODULE_KEYS = Object.keys(MODULE_DEFS);
